import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  bulkMoveVaultItems as bulkMoveVaultItemsApi,
  bulkReassignVaultOwner as bulkReassignVaultOwnerApi,
  convertPdfToDocx as convertPdfToDocxApi,
  convertVaultFileToContract as convertVaultFileToContractApi,
  createVaultDirectory,
  deleteContractManagementContract,
  createVaultFile,
  deleteVaultDirectory,
  deleteVaultFile,
  deleteVaultFileRevision,
  finalizeVaultFileRevisionUpload,
  getVaultDirectory as getVaultDirectoryApi,
  getVaultAncestors,
  getVaultFileRevisionDiff,
  initializeVaultFileRevisionUpload,
  listVaultFileRevisions,
  updateVaultFileRevision,
  uploadVaultFileChunk,
  unpackVaultFile,
  updateVaultDirectory,
  updateVaultFile,
  PreprocessingTaskStatus,
  type VaultDirectoryCreateUser,
  type VaultDirectoryInDb,
  type VaultDirectoryUpdateUser,
  type VaultFileCreateUser,
  type VaultFileInDb,
  type VaultFileRevisionDiff,
  type VaultFileRevisionInDb,
  type VaultFileRevisionListResponse,
  type VaultFileRevisionUpdateUser,
  type VaultFileSignatureInfoInDb,
  type VaultFileUpdateUser,
  type ApplicationMessage,
  type ContractManagementContractStates,
  type GetVaultDirectoryData,
} from '@/api';
import { useAuthStore } from '@/stores/auth';
import { isEmptyVaultGroups, type VaultUidGroups } from '@/utils/vaultSelection';
import { useOrganizationStore } from './organizationStore';
import { ApiError, extractErrorDetail, isAccessDenied } from '@/utils/errorHandler';
import { getUploadSizeError } from '@/utils/fileUploadValidation';
import { executeRateLimitedInit } from '@/utils/uploadInitLimiter';
import { mapVaultFileToVaultItem } from '@/utils/vaultItemMapping';
import {
  fetchWholeDirectory,
  type ListedContract,
  type ListedDirectory,
  type ListedFile,
} from '@/services/vaultListingService';

/** Minimal owner info for display (matches API UserInDb subset) */
export interface VaultFileOwnerDisplay {
  uid: string;
  full_name: string;
  email: string;
}

export interface VaultItem {
  key: string;
  name: string;
  type: 'directory' | 'file' | 'contract';
  size?: number;
  lastModified?: string;
  /**
   * Recursive count of items (plain files and contracts, a contract counting
   * once) in this directory and all of its subdirectories. Only meaningful for `directory` items, and only populated
   * by the directory listing endpoint (`getVaultDirectory`). `null`/`undefined`
   * when the count was not computed for this response.
   */
  fileCount?: number | null;
  children?: VaultItem[];
  path?: string[];
  icon?: string;
  contentType?: string;
  previewStatus?: PreprocessingTaskStatus | null;
  ocrStatus?: PreprocessingTaskStatus | null;
  conversionStatus?: PreprocessingTaskStatus | null;
  /** Item owner (default none). An unowned item is org admins only. */
  owner_user?: VaultFileOwnerDisplay | null;
  /**
   * A file whose conversion into a contract is running: the backend refuses
   * to rename, move, delete or re-version it (409) until it is done.
   */
  converting?: boolean;
  /**
   * The listing's type label: 'folder', 'archive', 'file', or the contract's
   * type ("NDA", "MSA", …). What the Type column shows and sorts by.
   */
  typeLabel?: string;
  /** How many files the contract holds; contracts only. */
  contractFileCount?: number;
  /**
   * The caller's effective bits on the item (READ=1, WRITE=2, ADMIN=4); null
   * when the response did not compute them, which gates nothing.
   */
  permissions?: number | null;
  versionNumber?: number;
  /** A contract-generation draft: opens in the wizard, not the document viewer. */
  isDraft?: boolean;
  revisionUid?: string;
  signatureInfos?: VaultFileSignatureInfoInDb[] | null;
  /** A contract's resolved contract and amendment states; contracts only. */
  contractStates?: ContractManagementContractStates | null;
}

/** The order a folder listing is sorted in on the server. */
export interface VaultListingSort {
  key: NonNullable<NonNullable<GetVaultDirectoryData['query']>['sort_key']>;
  direction: 'asc' | 'desc';
}

export const useVaultStore = defineStore('vault', () => {
  // State
  const authStore = useAuthStore();
  const isLoading = ref<boolean>(false);
  const currentDirectory = ref<VaultDirectoryInDb | null>(null);
  const directoryPath = ref<VaultItem[]>([]);
  // Uid of the main vault root, learned from a root request (no uid). Not from "any
  // parentless directory": the system vault's root has no parent either.
  // Root detection must always go through this — never `parent_directory === null`
  // (also null when the parent is unreadable) and never "first segment of the trail": the ancestors endpoint trims unreadable ancestors, so
  // a trimmed trail starts at a regular folder, and the root's real `directory_name` is
  // an internal `organisation_<id>_root` that must never be rendered.
  const rootDirectoryUid = ref<string | null>(null);
  const items = ref<VaultItem[]>([]);
  // Null is the server's default: by name, ascending. Folders, contracts and
  // files are sorted together, as one sequence.
  const listingSort = ref<VaultListingSort | null>(null);
  // The open folder is listed one page at a time; `itemsCount` is its total.
  const listingPage = ref<{ page: number; pageSize: number }>({ page: 1, pageSize: 50 });
  const itemsCount = ref<number>(0);
  const selectedItems = ref<string[]>([]);
  const uploadProgress = ref<{ [key: string]: number }>({});

  const errors = ref<string[]>([]);

  // Organization store for credits usage updates
  const organizationStore = useOrganizationStore();

  // Getters
  const currentDirectoryUid = computed<string | null>(() => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
    return currentDirectory.value?.uid || null;
  });

  const hasSelectedItems = computed<boolean>(() => {
    return selectedItems.value.length > 0;
  });

  const getItemByKey = computed(() => {
    return (key: string): VaultItem | undefined => {
      return findItem(items.value, key);
    };
  });

  // Helper function to find item by key
  function findItem(items: VaultItem[], key: string): VaultItem | undefined {
    for (const item of items) {
      if (item.key === key) {
        return item;
      }
      if (item.children) {
        const found = findItem(item.children, key);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  // Convert API models to VaultItem format
  function convertToVaultItems(directoryData: VaultDirectoryInDb): VaultItem[] {
    const result: VaultItem[] = [];

    // One sequence, in the server's order, whatever the kind.
    for (const item of directoryData.items) {
      if (item.kind === 'directory') {
        result.push(directoryToItem(item));
      } else if (item.kind === 'contract') {
        result.push(contractToItem(item));
      } else {
        result.push(fileToItem(item));
      }
    }

    return result;
  }

  function directoryToItem(dir: ListedDirectory): VaultItem {
    return {
      key: `dir-${dir.uid}`,
      name: dir.directory_name || 'Unnamed Directory',
      type: 'directory',
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      lastModified: dir.updated_at || dir.created_at,
      fileCount: dir.file_count ?? null,
      permissions: dir.permissions ?? null,
      owner_user: dir.owner_user
        ? {
            uid: dir.owner_user.uid,
            full_name: dir.owner_user.full_name,
            email: dir.owner_user.email,
          }
        : null,
      path: [], // We don't have path information in the API model
      children: [],
      typeLabel: dir.type_label,
    };
  }

  // A contract is a leaf: its files are reached through its detail page and
  // never appear in the folder's listing.
  function contractToItem(contract: ListedContract): VaultItem {
    return {
      key: `contract-${contract.uid}`,
      name: contract.name,
      type: 'contract',
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      lastModified: contract.updated_at || contract.created_at,
      contractFileCount: contract.contract_file_count,
      permissions: contract.permissions ?? null,
      owner_user: contract.owner_user
        ? {
            uid: contract.owner_user.uid,
            full_name: contract.owner_user.full_name,
            email: contract.owner_user.email,
          }
        : null,
      path: [],
      typeLabel: contract.type_label,
      contractStates: contract.states,
    };
  }

  function fileToItem(file: ListedFile): VaultItem {
    // Field derivation is shared with the file picker; see
    // `src/utils/vaultItemMapping.ts`.
    const mapped = mapVaultFileToVaultItem(file);
    return {
      key: mapped.key,
      name: mapped.name || 'Unnamed File',
      type: 'file',
      size: mapped.size,
      lastModified: mapped.lastModified,
      contentType: mapped.contentType,
      path: [],
      previewStatus: mapped.previewStatus,
      ocrStatus: mapped.ocrStatus,
      conversionStatus: mapped.conversionStatus,
      // Download + version endpoints work off the revision uid, not the
      // file uid — `downloadFile` below would otherwise fall back to
      // stripping `file-` off the key (which yields the file uid) and
      // 404.
      revisionUid: mapped.revisionUid,
      owner_user: mapped.ownerUser,
      versionNumber: mapped.versionNumber,
      isDraft: mapped.isDraft,
      signatureInfos: mapped.signatureInfos,
      converting: mapped.converting,
      permissions: mapped.permissions,
      typeLabel: file.type_label,
    };
  }

  // Resolve the ancestor breadcrumb segments (root-first, excluding the directory itself)
  // for a freshly loaded directory. Regular navigation derives the chain locally from the
  // existing trail; only when the path is unknown (deep link, hard refresh) does this hit
  // the dedicated ancestors endpoint — the backend intentionally no longer ships the chain
  // with every directory response.
  async function resolveAncestorSegments(directory: VaultDirectoryInDb): Promise<VaultItem[]> {
    // The root, or a folder whose parent the user cannot read: the backend's
    // trail would be empty either way.
    if (!directory.parent_directory) {
      return [];
    }

    const currentPath = directoryPath.value;

    // Breadcrumb click, parent navigation, or refresh of a directory already in the
    // trail: truncate to everything above it.
    const selfIndex = currentPath.findIndex(segment => segment.key === `dir-${directory.uid}`);
    if (selfIndex !== -1) {
      return currentPath.slice(0, selfIndex);
    }

    // Stepping into a child of the directory we're on: the current trail is the chain.
    if (currentPath.at(-1)?.key === `dir-${directory.parent_directory.uid}`) {
      return [...currentPath];
    }

    // Path unknown — fetch the chain (trimmed to ancestors the user can read). Whether
    // its first segment is the vault root is decided by uid, not position;
    // updateDirectoryPath has learned the root uid by now.
    const response = await getVaultAncestors({
      query: { kind: 'directory', uid: directory.uid },
    });
    return (response.data ?? []).map(ancestor => ({
      key: `dir-${ancestor.uid}`,
      name: ancestor.uid === rootDirectoryUid.value ? 'Home' : ancestor.name,
      type: 'directory' as const,
      path: [],
    }));
  }

  // Update breadcrumb path for a freshly loaded directory. When the chain is empty the
  // requested directory is the root itself.
  async function updateDirectoryPath(directory: VaultDirectoryInDb): Promise<void> {
    // The root by uid, never by a missing parent: a folder whose parent the user
    // cannot read has no parent_directory either, and keeps its own name.
    const isRoot = directory.uid === (await resolveRootDirectoryUid());
    const pathSegments: VaultItem[] = [
      ...(await resolveAncestorSegments(directory)),
      {
        key: `dir-${directory.uid}`,
        name: isRoot ? 'Home' : directory.directory_name || 'Current Directory',
        type: 'directory',
        path: [],
      },
    ];

    // Build the full paths. At this point, pathSegments is already ordered root -> current.
    let fullPath: string[] = [];
    directoryPath.value = pathSegments.map(segment => {
      fullPath = [...fullPath, segment.name];
      return { ...segment, path: [...fullPath] };
    });
  }

  // Actions
  async function getVaultDirectory(directoryUid?: string): Promise<VaultItem[]> {
    isLoading.value = true;
    errors.value = [];

    try {
      const { page, pageSize } = listingPage.value;
      const response = await getVaultDirectoryApi({
        query: {
          ...(directoryUid ? { directory_uid: directoryUid } : {}),
          ...(listingSort.value
            ? { sort_key: listingSort.value.key, sort_direction: listingSort.value.direction }
            : {}),
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
      });
      if (!response.data) {
        throw new Error('Failed to fetch vault directory');
      }
      const directory = response.data;

      // The page ran past the end (its last items were deleted or moved away):
      // step back to the folder's last page.
      if (directory.items.length === 0 && page > 1 && directory.items_count > 0) {
        listingPage.value = {
          page: Math.ceil(directory.items_count / pageSize),
          pageSize,
        };
        return await getVaultDirectory(directoryUid);
      }

      if (!directoryUid) {
        rootDirectoryUid.value = directory.uid;
      }

      // Only update current directory and path if this is a root level navigation
      // (when no parent directory is provided or this is a top-level call)
      if (
        !directoryUid ||
        currentDirectory.value === null ||
        directoryUid === currentDirectoryUid.value
      ) {
        currentDirectory.value = directory;
        await updateDirectoryPath(directory);
      }

      const vaultItems = convertToVaultItems(directory);

      // Only update items array if this is a root level navigation
      if (!directoryUid || directoryUid === currentDirectoryUid.value) {
        items.value = vaultItems;
        itemsCount.value = directory.items_count;
      }

      return vaultItems;
    } catch (error) {
      // A refused folder is the caller's to render, not a toast.
      if (!isAccessDenied(error)) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load directory';
        errors.value.push(errorMessage);
      }
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  async function createDirectory(directoryData: {
    name: string;
    parent_directory_uid?: string;
  }): Promise<VaultDirectoryInDb> {
    isLoading.value = true;
    errors.value = [];

    try {
      // No parent means the vault root, never the folder a vault view happens to show.
      const parentDirectoryUid =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to the root
        directoryData.parent_directory_uid || (await resolveRootDirectoryUid());

      // Create the payload with properties that match the VaultDirectoryCreateUser type
      const createData: VaultDirectoryCreateUser = {
        directory_name: directoryData.name,
        parent_directory_uid: parentDirectoryUid,
      };

      const response = await createVaultDirectory({ body: createData });
      if (!response.data) {
        throw new Error('Failed to create directory');
      }
      const newDirectory = response.data;

      // Refresh the current directory to show the new folder
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      await getVaultDirectory(currentDirectoryUid.value || undefined);

      return newDirectory;
    } catch (error) {
      const errorMessage = extractErrorDetail(error, 'Failed to create directory');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  async function updateDirectory(
    directoryUid: string,
    updateData: { name?: string }
  ): Promise<VaultDirectoryInDb> {
    isLoading.value = true;
    errors.value = [];

    try {
      // Create the payload with properties that match the VaultDirectoryUpdateUser type.
      // Moving a directory goes through bulkMoveVaultItems, not this endpoint.
      const updateParams: VaultDirectoryUpdateUser = {
        directory_name: updateData.name,
      };

      const response = await updateVaultDirectory({
        path: { uid: directoryUid },
        body: updateParams,
      });
      if (!response.data) {
        throw new Error('Failed to update directory');
      }
      const updatedDirectory = response.data;

      // Refresh the current directory
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      await getVaultDirectory(currentDirectoryUid.value || undefined);

      return updatedDirectory;
    } catch (error) {
      // Surface the backend's detail (e.g. pinned system directories, data rooms).
      const errorMessage = extractErrorDetail(error, 'Failed to update directory');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteDirectory(directoryUid: string): Promise<void> {
    isLoading.value = true;
    errors.value = [];

    try {
      await deleteVaultDirectory({ path: { uid: directoryUid } });

      // If we're deleting the current directory, navigate to its parent
      if (directoryUid === currentDirectoryUid.value) {
        const parentDirectoryUid = currentDirectory.value?.parent_directory?.uid;

        // If there's a parent, navigate to it; otherwise, go to the root
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
        await getVaultDirectory(parentDirectoryUid || undefined);
      } else {
        // Refresh the current directory
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
        await getVaultDirectory(currentDirectoryUid.value || undefined);
      }
    } catch (error) {
      // Surface the backend's detail (e.g. pinned system directories, data rooms).
      const errorMessage = extractErrorDetail(error, 'Failed to delete directory');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Uid of the vault root — also where a file or folder created without a parent
   * goes. Never the folder a vault view happens to show: an upload started
   * elsewhere has nothing to do with where the user last browsed. Sets no other
   * state, so it can run while a vault view is mounted elsewhere.
   */
  async function resolveRootDirectoryUid(): Promise<string> {
    if (rootDirectoryUid.value) return rootDirectoryUid.value;

    const response = await getVaultDirectoryApi();
    if (!response.data) throw new Error('Failed to fetch root directory');
    rootDirectoryUid.value = response.data.uid;
    return response.data.uid;
  }

  /**
   * Create an empty file anchor in a folder (default: the vault root), or
   * straight in a contract, where it joins as a new contract file that the AI
   * classifies once its first revision is uploaded.
   */
  async function createEmptyFile(
    target?: string | { contractUid: string }
  ): Promise<VaultFileInDb> {
    isLoading.value = true;
    errors.value = [];

    try {
      const createBody: VaultFileCreateUser =
        typeof target === 'object'
          ? { contract_uid: target.contractUid }
          : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to the root
            { parent_directory_uid: target || (await resolveRootDirectoryUid()) };
      const response = await createVaultFile({ body: createBody });
      if (!response.data) throw new Error('Failed to create vault file');
      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create vault file';
      errors.value.push(errorMessage);
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  async function uploadFileChunk(
    uploadId: string,
    chunk: Blob,
    index: number,
    totalChunks: number
  ): Promise<void> {
    errors.value = [];

    try {
      const apiFormData = {
        chunk: new Blob([chunk], { type: 'application/octet-stream' }),
      };

      await uploadVaultFileChunk({
        path: { upload_id: uploadId },
        body: apiFormData,
      });

      // Update progress
      uploadProgress.value[uploadId] = Math.round((index / totalChunks) * 100);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload file chunk';
      errors.value.push(errorMessage);
      throw error;
    }
  }

  async function updateFile(
    fileUid: string,
    updateData: {
      name?: string;
      /** Reassign owner (omit to leave unchanged). Unassign only via bulk. */
      owner_user_uid?: string | null;
    }
  ): Promise<VaultFileInDb> {
    isLoading.value = true;
    errors.value = [];

    try {
      // Moving a file goes through bulkMoveVaultItems, not this endpoint.
      const updateParams: VaultFileUpdateUser = {
        ...(updateData.name !== undefined && { file_name: updateData.name }),
        ...(updateData.owner_user_uid !== undefined && {
          owner_user_uid: updateData.owner_user_uid,
        }),
      };

      const response = await updateVaultFile({
        path: { uid: fileUid },
        body: updateParams,
      });

      if (!response.data) {
        throw new Error('Failed to update file');
      }
      const updatedFile = response.data;

      // Refresh the current directory
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      await getVaultDirectory(currentDirectoryUid.value || undefined);

      return updatedFile;
    } catch (error) {
      // The backend's own words, e.g. the 409 while the file converts.
      const errorMessage = extractErrorDetail(error, 'Failed to update file');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteFile(fileUid: string): Promise<void> {
    isLoading.value = true;
    errors.value = [];

    try {
      await deleteVaultFile({ path: { uid: fileUid } });

      // Refresh the current directory
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      await getVaultDirectory(currentDirectoryUid.value || undefined);
    } catch (error) {
      // Surface the backend's detail (e.g. pinned system directories, data rooms).
      const errorMessage = extractErrorDetail(error, 'Failed to delete file');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  /** Bulk reassign or unassign owners. Org admins only; pass null to unassign. Supports folders: when directoryUids is provided, backend reassigns the folders themselves (org root excluded) — with recursive (the default) their whole subtree and all contained files too. Returns the backend message ("... N file(s) and M directory(ies)"). */
  /**
   * Reassign (or, with null, clear) the owner of files, contracts and folders
   * in one go. Org admins only. A contract's files follow the contract; with
   * `recursive`, a folder brings its subfolders, files and contracts.
   */
  async function bulkReassignVaultOwner(
    groups: VaultUidGroups,
    ownerUserUid: string | null,
    recursive: boolean = true
  ): Promise<ApplicationMessage | null> {
    if (isEmptyVaultGroups(groups)) return null;
    isLoading.value = true;
    errors.value = [];

    try {
      const response = await bulkReassignVaultOwnerApi({
        body: {
          file_uids: groups.fileUids,
          contract_uids: groups.contractUids,
          directory_uids: groups.directoryUids,
          owner_user_uid: ownerUserUid,
          recursive,
        },
      });
      // Also used outside the vault: refresh only an open folder.
      if (currentDirectory.value) await getVaultDirectory(currentDirectory.value.uid);
      return response.data ?? null;
    } catch (error) {
      const errorMessage = extractErrorDetail(error, 'Failed to reassign owners');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Move files, contracts (with all their files) and folders into a folder.
   * Needs ADMIN on every item and WRITE on the target.
   */
  async function bulkMoveVaultItems(
    groups: VaultUidGroups,
    targetDirectoryUid: string
  ): Promise<void> {
    if (isEmptyVaultGroups(groups)) return;
    isLoading.value = true;
    errors.value = [];

    try {
      await bulkMoveVaultItemsApi({
        body: {
          file_uids: groups.fileUids,
          contract_uids: groups.contractUids,
          directory_uids: groups.directoryUids,
          target_directory_uid: targetDirectoryUid,
        },
      });
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      await getVaultDirectory(currentDirectoryUid.value || undefined);
    } catch (error) {
      // Surface the backend's detail (e.g. pinned system directories, data rooms).
      const errorMessage = extractErrorDetail(error, 'Failed to move items');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  /** Deletes a contract and all its files. */
  async function deleteContract(contractUid: string): Promise<void> {
    isLoading.value = true;
    errors.value = [];

    try {
      await deleteContractManagementContract({ path: { uid: contractUid } });
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      await getVaultDirectory(currentDirectoryUid.value || undefined);
    } catch (error) {
      const errorMessage = extractErrorDetail(error, 'Failed to delete contract');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  async function downloadFile(
    revisionUid: string,
    contentType: string = 'application/octet-stream'
  ): Promise<Blob> {
    isLoading.value = true;
    errors.value = [];

    try {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const url = `${apiUrl}/v1/app/repository/vault/file/revision/${revisionUid}`;

      const token = await authStore.user?.getIdToken();
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        // A draft that no longer renders answers 422 with a detail.
        throw new ApiError(response.status, await response.json().catch(() => null));
      }

      return new Blob([await response.arrayBuffer()], { type: contentType });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download file';
      errors.value.push(errorMessage);
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  async function unpackFile(fileUid: string): Promise<VaultDirectoryInDb> {
    isLoading.value = true;
    errors.value = [];

    try {
      const response = await unpackVaultFile({ path: { uid: fileUid } });

      if (!response.data) {
        throw new Error('Failed to unpack file');
      }
      const result = response.data;

      // Refresh the current directory
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      await getVaultDirectory(currentDirectoryUid.value || undefined);

      // Update organization allowance usage after successful operation
      if (organizationStore.activeOrganization?.uid) {
        await organizationStore.fetchOwnActiveOrganizationAllowanceUsage();
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unpack file';
      errors.value.push(errorMessage);
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  async function convertPdfToDocx(fileUid: string): Promise<ApplicationMessage> {
    isLoading.value = true;
    errors.value = [];

    try {
      const response = await convertPdfToDocxApi({
        path: { uid: fileUid },
        throwOnError: false,
      });

      if (!response.data) {
        throw new Error(extractErrorDetail(response.error, 'Failed to convert file to DOCX'));
      }

      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      await getVaultDirectory(currentDirectoryUid.value || undefined);

      return response.data;
    } catch (error) {
      const errorMessage = extractErrorDetail(error, 'Failed to convert file to DOCX');
      errors.value.push(errorMessage);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  /** Starts the background conversion of a plain file into a contract (needs ADMIN on the file). */
  async function convertFileToContract(fileUid: string): Promise<void> {
    const response = await convertVaultFileToContractApi({
      path: { uid: fileUid },
      throwOnError: false,
    });
    if (!response.data) {
      throw new Error(extractErrorDetail(response.error, 'Failed to convert file to contract'));
    }
    // The row shows `converting` until the task ends; the vault_directory_updated
    // event refreshes the listing again once the file has become a contract.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
    await getVaultDirectory(currentDirectoryUid.value || undefined);
  }

  function setSelectedItems(items: string[]): void {
    selectedItems.value = items;
  }

  function clearErrors(): void {
    errors.value = [];
  }

  // Select a directory and update the current path
  async function selectDirectory(directoryUid: string): Promise<VaultItem[]> {
    resetPageOnFolderChange(directoryUid);
    return getVaultDirectory(directoryUid);
  }

  /** Another folder opens on its first page; a refresh of the open one keeps its page. */
  function resetPageOnFolderChange(directoryUid: string | null): void {
    if ((directoryUid ?? rootDirectoryUid.value) !== currentDirectoryUid.value) {
      listingPage.value = { ...listingPage.value, page: 1 };
    }
  }

  /** Re-sorts the open folder on the server, from its first page; later listings keep the order. */
  async function setListingSort(sort: VaultListingSort | null): Promise<void> {
    listingSort.value = sort;
    listingPage.value = { ...listingPage.value, page: 1 };
    if (currentDirectory.value) await getVaultDirectory(currentDirectory.value.uid);
  }

  /** Shows another page of the open folder, or the same folder in pages of another size. */
  async function setListingPage(page: number, pageSize: number): Promise<void> {
    listingPage.value = { page, pageSize };
    if (currentDirectory.value) await getVaultDirectory(currentDirectory.value.uid);
  }

  /**
   * Every item of a folder, all pages, without touching the open listing —
   * for callers that need the whole folder (a duplicate check, a picker).
   */
  async function getWholeDirectoryItems(directoryUid?: string): Promise<VaultItem[]> {
    return convertToVaultItems(await fetchWholeDirectory(directoryUid));
  }

  // Navigate into a new directory for flat (non-tree) views.
  // Resets currentDirectory so the guard in getVaultDirectory passes and items/path update.
  async function navigateFlatToDirectory(directoryUid: string | null): Promise<VaultItem[]> {
    resetPageOnFolderChange(directoryUid);
    currentDirectory.value = null;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
    return getVaultDirectory(directoryUid || undefined);
  }

  // Select a directory from the breadcrumb path
  async function selectDirectoryFromPath(index: number): Promise<VaultItem[]> {
    if (index >= 0 && index < directoryPath.value.length) {
      const selectedDirectory = directoryPath.value[index];
      const uid = selectedDirectory.key.replace('dir-', '');
      return selectDirectory(uid);
    }
    return [];
  }

  // Navigate to the parent directory
  async function navigateToParentDirectory(): Promise<VaultItem[]> {
    if (currentDirectory.value?.parent_directory?.uid) {
      return selectDirectory(currentDirectory.value.parent_directory.uid);
    }
    return [];
  }

  async function listRevisions(
    fileUid: string,
    limit = 50,
    offset = 0
  ): Promise<VaultFileRevisionListResponse> {
    const response = await listVaultFileRevisions({
      path: { uid: fileUid },
      query: { limit, offset },
    });
    if (!response.data) throw new Error('Failed to list revisions');
    return response.data;
  }

  async function initializeRevisionUpload(
    fileUid: string,
    data: { file_name: string; total_bytes: number; change_description?: string | null }
  ): Promise<string> {
    const sizeError = getUploadSizeError(data.file_name, data.total_bytes);
    if (sizeError) throw new Error(sizeError);

    // Override the global `throwOnError: true` (src/config/api.ts) so we can
    // read the status/headers off the response — needed to detect 429 and
    // honor the rate-limit headers.
    return executeRateLimitedInit<string>(async () => {
      const response = await initializeVaultFileRevisionUpload({
        path: { uid: fileUid },
        body: data,
        throwOnError: false,
      });
      const status = response.response?.status ?? 0;
      const headers = response.response?.headers;

      if (status === 429) {
        return { status, headers, value: undefined };
      }
      if (!response.data) {
        throw new Error(extractErrorDetail(response.error, 'Failed to initialize revision upload'));
      }
      return { status, headers, value: response.data.upload_id };
    });
  }

  async function finalizeRevisionUpload(
    fileUid: string,
    uploadId: string
  ): Promise<VaultFileRevisionInDb> {
    const response = await finalizeVaultFileRevisionUpload({
      path: { uid: fileUid, upload_id: uploadId },
    });
    if (!response.data) throw new Error('Failed to finalize revision upload');
    return response.data;
  }

  async function deleteRevision(fileUid: string, revisionUid: string): Promise<void> {
    await deleteVaultFileRevision({ path: { uid: fileUid, revision_uid: revisionUid } });
  }

  async function updateRevision(
    fileUid: string,
    revisionUid: string,
    data: VaultFileRevisionUpdateUser
  ): Promise<VaultFileRevisionInDb> {
    const response = await updateVaultFileRevision({
      path: { uid: fileUid, revision_uid: revisionUid },
      body: data,
    });
    if (!response.data) throw new Error('Failed to update revision');
    return response.data;
  }

  async function getRevisionDiff(
    fileUid: string,
    revisionUid: string,
    compareToRevisionUid: string
  ): Promise<VaultFileRevisionDiff> {
    const response = await getVaultFileRevisionDiff({
      path: { uid: fileUid, revision_uid: revisionUid },
      query: { compare_to_revision_uid: compareToRevisionUid },
    });
    if (!response.data) throw new Error('Failed to get revision diff');
    return response.data;
  }

  return {
    // State
    isLoading,
    currentDirectory,
    directoryPath,
    listingSort,
    setListingSort,
    listingPage,
    itemsCount,
    setListingPage,
    getWholeDirectoryItems,
    rootDirectoryUid,
    items,
    selectedItems,
    uploadProgress,
    errors,

    // Getters
    currentDirectoryUid,
    hasSelectedItems,
    getItemByKey,

    // Actions
    getVaultDirectory,
    createDirectory,
    updateDirectory,
    deleteDirectory,
    createEmptyFile,
    resolveRootDirectoryUid,
    uploadFileChunk,
    updateFile,
    deleteFile,
    bulkReassignVaultOwner,
    deleteContract,
    bulkMoveVaultItems,
    downloadFile,
    unpackFile,
    convertPdfToDocx,
    convertFileToContract,
    setSelectedItems,
    clearErrors,
    selectDirectory,
    navigateFlatToDirectory,
    selectDirectoryFromPath,
    navigateToParentDirectory,
    listRevisions,
    initializeRevisionUpload,
    finalizeRevisionUpload,
    deleteRevision,
    updateRevision,
    getRevisionDiff,
  };
});
